import type { CandidatePlace, PlaceSnapshot } from '@/types/gmb';

interface SnapshotSectionProps {
  /** 是否正在取得商家公開資料 */
  loading: boolean;
  /** 已取得的商家公開資料快照 */
  snapshot: PlaceSnapshot | null;
  /** 取得失敗時的錯誤訊息（後端回傳的中文訊息） */
  errorMessage: string | null;
  /** 使用者選擇的候選店家（載入 / 失敗時顯示店名） */
  candidate: CandidatePlace | null;
}

/** 服務區域商家：沒有公開實體地址屬正常狀況。 */
function ServiceAreaTag() {
  return (
    <span className="ds-badge bg-accent-100 text-accent-800 ring-1 ring-accent-200">
      <i className="ri-service-line" />
      服務區域商家
    </span>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2.5 text-base">
      <i className={`${icon} text-lg text-primary-700`} />
      <span className="text-foreground-600">{label}</span>
      <span className="min-w-0 truncate font-semibold text-foreground-900">{value}</span>
    </div>
  );
}

/** 選擇商家後：取得 Google 商家公開資料的載入 / 成功 / 失敗狀態。 */
export default function SnapshotSection({ loading, snapshot, errorMessage, candidate }: SnapshotSectionProps) {
  if (loading) {
    return (
      <section className="ds-container pt-8">
        <div className="rounded-2xl border border-background-300 bg-background-50 p-5 md:p-6">
          <div className="flex items-center gap-4">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-700" />
            <div className="min-w-0">
              <p className="text-base font-bold text-foreground-900">正在整理商家公開資料…</p>
              {candidate && <p className="mt-1 truncate text-base text-foreground-600">{candidate.name}</p>}
            </div>
          </div>
          <div className="mt-4 space-y-2.5">
            <div className="h-5 w-2/3 animate-pulse rounded-md bg-background-200" />
            <div className="h-5 w-1/2 animate-pulse rounded-md bg-background-200" />
          </div>
        </div>
      </section>
    );
  }

  if (errorMessage) {
    return (
      <section className="ds-container pt-8">
        <div className="flex items-start gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800 md:p-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50/80">
            <i className="ri-error-warning-line text-xl" />
          </span>
          <div className="min-w-0">
            <p className="text-base font-bold md:text-lg">無法取得商家公開資料</p>
            <p className="mt-1.5 text-base leading-relaxed opacity-90">{errorMessage}</p>
            <p className="mt-3 flex items-start gap-2 text-base font-medium">
              <i className="ri-arrow-right-circle-line mt-0.5 text-lg" />
              <span>請確認選擇的店家是否正確，或回到上方重新搜尋後再試一次。</span>
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (!snapshot) return null;

  const name = snapshot.displayName || candidate?.name || '';
  const category = snapshot.primaryTypeDisplayName || snapshot.primaryType || candidate?.primaryCategory || '';
  const isServiceArea = snapshot.pureServiceAreaBusiness === true;
  const hasAddress = typeof snapshot.formattedAddress === 'string' && snapshot.formattedAddress.length > 0;

  return (
    <section className="ds-container pt-8">
      <div className="rounded-2xl border border-primary-200 bg-primary-50/70 p-5 md:p-6">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50 text-primary-700 ring-1 ring-primary-200">
            <i className="ri-checkbox-circle-line text-xl" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="text-base font-bold text-primary-900">商家資料取得成功</p>
              {isServiceArea && <ServiceAreaTag />}
            </div>
            <p className="mt-1.5 truncate text-lg font-bold text-foreground-950 md:text-xl">{name}</p>

            <div className="mt-4 space-y-2">
              {category && <InfoRow icon="ri-price-tag-3-line" label="主要類別" value={category} />}
              {hasAddress ? (
                <InfoRow icon="ri-map-pin-line" label="地址" value={snapshot.formattedAddress as string} />
              ) : isServiceArea ? (
                <p className="flex items-center gap-2.5 text-base text-foreground-600">
                  <i className="ri-map-pin-2-line text-lg" />
                  此商家未公開實體地址
                </p>
              ) : null}
            </div>

            <p className="mt-4 flex items-center gap-2 text-base text-primary-800">
              <i className="ri-information-line text-lg" />
              已成功取得 Google 商家公開資料，下一階段將進行同業比較。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}